import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { teamApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Search, Briefcase, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const DRAGON_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/ot58j39v_Transparent%20BG%20Dragon.png';

const Team = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await teamApi.getAll();
        setProfiles(response.data);
      } catch (error) {
        toast.error('Failed to fetch team members');
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  const filteredProfiles = profiles.filter(profile =>
    profile.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
    profile.title?.toLowerCase().includes(search.toLowerCase()) ||
    profile.expertise_areas?.some(e => e.toLowerCase().includes(search.toLowerCase()))
  );

  const getAvatarColor = (name) => {
    const colors = ['#106EFD', '#40CFDE', '#37C563', '#FF801D', '#E171EA', '#FFD166'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  };

  const getUtilizationColor = (utilization) => {
    if (utilization > 100) return 'bg-[#FF801D]';
    if (utilization > 80) return 'bg-[#FFD166]';
    return 'bg-[#37C563]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Team Directory</h1>
          <p className="text-navy/60 mt-1">View team members and their expertise</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
        <Input
          placeholder="Search by name, title, or expertise..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="team-search-input"
        />
      </div>

      {/* Team grid */}
      {filteredProfiles.length === 0 ? (
        <div className="empty-state py-16">
          <img src={DRAGON_MASCOT} alt="Dragon mascot" className="w-32 h-32 mb-4" />
          <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No team members found</h3>
          <p className="text-navy/60">
            {search ? 'Try a different search term' : 'Team profiles will appear here'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProfiles.map((profile) => {
            const utilization = (profile.current_allocated_hours / profile.weekly_capacity_hours) * 100;
            
            return (
              <Link key={profile.id} to={`/team/${profile.user_id}`}>
                <Card className="bg-white border-navy/10 card-hover h-full" data-testid={`team-card-${profile.user_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-14 h-14">
                        <AvatarImage src={profile.avatar_url} />
                        <AvatarFallback 
                          className="font-bold text-white"
                          style={{ backgroundColor: getAvatarColor(profile.user?.name) }}
                        >
                          {getInitials(profile.user?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#1A2B5E] truncate">{profile.user?.name}</h3>
                        <p className="text-sm text-navy/60 truncate">{profile.title || 'Team Member'}</p>
                        <Badge 
                          className="mt-2 capitalize"
                          variant="outline"
                        >
                          {profile.user?.role?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>

                    {/* Utilization bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-navy/60 mb-1">
                        <span>Capacity</span>
                        <span>{Math.round(utilization)}% utilized</span>
                      </div>
                      <div className="utilization-bar">
                        <div 
                          className={`utilization-fill ${getUtilizationColor(utilization)}`}
                          style={{ width: `${Math.min(utilization, 100)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-navy/50 mt-1">
                        <span>{profile.current_allocated_hours || 0}h allocated</span>
                        <span>{profile.weekly_capacity_hours || 40}h capacity</span>
                      </div>
                    </div>

                    {/* Expertise */}
                    {profile.expertise_areas?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-navy/50 mb-2 flex items-center gap-1">
                          <Briefcase className="w-3 h-3" /> Expertise
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {profile.expertise_areas.slice(0, 3).map((area, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {area}
                            </Badge>
                          ))}
                          {profile.expertise_areas.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{profile.expertise_areas.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Jurisdictions */}
                    {profile.jurisdictions?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-navy/50 mb-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Jurisdictions
                        </p>
                        <p className="text-xs text-navy/70">
                          {profile.jurisdictions.slice(0, 3).join(', ')}
                          {profile.jurisdictions.length > 3 && ` +${profile.jurisdictions.length - 3} more`}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Team;
